import React, { useCallback, useEffect, useState, useRef, createElement, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { ImageIcon, ArrowLeft, CheckCircle2, AlertTriangle, Loader2, Upload, Trash2, InfoIcon, Archive, FileText, Search } from 'lucide-react';
// Dynamic imports for heavy libraries (loaded on demand)
async function loadJSZip() {
  const mod = await import('https://esm.sh/jszip@3.10.1' as any);
  return mod.default || mod;
}
async function loadMammoth() {
  const mod = await import('https://esm.sh/mammoth@1.6.0' as any);
  return mod.default || mod;
}
async function loadPdfjs() {
  const mod = await import('https://esm.sh/pdfjs-dist@3.11.174/build/pdf.min.js' as any);
  const pdfjsLib = mod.default || mod;
  pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  return pdfjsLib;
}
// ─── Types ───────────────────────────────────────────────────
interface RestaurantOption {
  id: number;
  name: string;
  slug: string;
}
interface MenuItemRow {
  id: string;
  name: string;
  image_url: string | null;
}
interface ImageEntry {
  file: File;
  preview: string;
  originalName: string;
  normalizedName: string;
  matchedItem: MenuItemRow | null;
  status: 'matched' | 'no-match';
  uploading: boolean;
  uploaded: boolean;
  publicUrl: string | null;
  error: string | null;
}
interface DocImageEntry {
  file: File;
  preview: string;
  detectedTitle: string;
  normalizedTitle: string;
  matchedItem: MenuItemRow | null;
  status: 'matched' | 'no-match' | 'manual-review';
  uploading: boolean;
  uploaded: boolean;
  publicUrl: string | null;
  error: string | null;
}
interface ApplyResult {
  total: number;
  matched: number;
  updated: number;
  unmatched: number;
}
type ImportMode = 'single' | 'zip' | 'doc';
// ─── Normalize ───────────────────────────────────────────────
function normalizeText(input: string): string {
  return (input || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove accents
  .replace(/\.[^/.]+$/, '') // remove file extension
  .replace(/[_-]+/g, ' ') // underscores/dashes → spaces
  .replace(/[^a-z0-9\s]/g, ' ') // special chars → spaces
  .replace(/\s+/g, ' ') // collapse spaces
  .trim();
}
// ─── Fuzzy Matching Utilities ────────────────────────────────
function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];
  for (let i = 0; i <= a.length; i++) matrix[i] = [i];
  for (let j = 0; j <= b.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);
    }
  }
  return matrix[a.length][b.length];
}
function wordOverlapScore(a: string, b: string): number {
  const wordsA = a.split(' ').filter(Boolean);
  const wordsB = b.split(' ').filter(Boolean);
  if (wordsA.length === 0 || wordsB.length === 0) return 0;
  let matches = 0;
  for (const wa of wordsA) {
    if (wordsB.some(wb => wb === wa || wb.includes(wa) || wa.includes(wb))) {
      matches++;
    }
  }
  return matches / Math.max(wordsA.length, wordsB.length);
}
function similarityScore(a: string, b: string): number {
  if (!a || !b) return 0;
  // Exact match
  if (a === b) return 1.0;
  // One contains the other
  if (a.includes(b) || b.includes(a)) {
    const ratio = Math.min(a.length, b.length) / Math.max(a.length, b.length);
    return 0.7 + ratio * 0.25; // 0.7–0.95 range
  }
  // Word overlap
  const overlap = wordOverlapScore(a, b);
  if (overlap >= 0.6) return 0.5 + overlap * 0.3; // 0.68–0.8 range
  // Levenshtein (only for short strings to avoid perf issues)
  if (a.length < 60 && b.length < 60) {
    const dist = levenshteinDistance(a, b);
    const maxLen = Math.max(a.length, b.length);
    const levScore = 1 - dist / maxLen;
    if (levScore > 0.6) return levScore * 0.7; // scaled down
  }
  return 0;
}
interface MatchResult {
  item: MenuItemRow | null;
  score: number;
  confidence: 'exact' | 'high' | 'medium' | 'low' | 'none';
}
function findBestMatch(normalizedInput: string, items: MenuItemRow[]): MatchResult {
  if (!normalizedInput) return {
    item: null,
    score: 0,
    confidence: 'none'
  };
  let bestItem: MenuItemRow | null = null;
  let bestScore = 0;
  for (const item of items) {
    const normalizedItem = normalizeText(item.name);
    const score = similarityScore(normalizedInput, normalizedItem);
    if (score > bestScore) {
      bestScore = score;
      bestItem = item;
    }
  }
  let confidence: MatchResult['confidence'] = 'none';
  if (bestScore >= 1.0) confidence = 'exact';else if (bestScore >= 0.75) confidence = 'high';else if (bestScore >= 0.5) confidence = 'medium';else if (bestScore >= 0.3) confidence = 'low';
  return {
    item: bestItem,
    score: bestScore,
    confidence
  };
}
function removeExtension(filename: string): string {
  return filename.replace(/\.[^.]+$/, '');
}
function safeFileName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9.]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}
// Helper to convert base64 to File
function base64ToFile(base64: string, filename: string): File {
  const arr = base64.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, {
    type: mime
  });
}
// ─── Page Component ──────────────────────────────────────────
export function EnrollmentMenuImagesPage() {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>('');
  const [isLoadingRestaurants, setIsLoadingRestaurants] = useState(true);
  const [menuItems, setMenuItems] = useState<MenuItemRow[]>([]);
  const [isLoadingMenu, setIsLoadingMenu] = useState(false);
  const [importMode, setImportMode] = useState<ImportMode>('single');
  // State for modes 1 & 2
  const [images, setImages] = useState<ImageEntry[]>([]);
  // State for mode 3
  const [docImages, setDocImages] = useState<DocImageEntry[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<ApplyResult | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // ─── Fetch Restaurants ─────────────────────────────────────
  useEffect(() => {
    async function fetchRestaurants() {
      try {
        const {
          data,
          error
        } = await supabase.from('restaurants').select('id, name, slug').eq('is_active', true).order('name');
        if (error) throw error;
        setRestaurants(data || []);
      } catch (err) {
        console.error('Error fetching restaurants:', err);
      } finally {
        setIsLoadingRestaurants(false);
      }
    }
    fetchRestaurants();
  }, []);
  // ─── Fetch Menu Items when restaurant changes ──────────────
  useEffect(() => {
    if (!selectedRestaurantId) {
      setMenuItems([]);
      return;
    }
    async function fetchMenu() {
      setIsLoadingMenu(true);
      try {
        const {
          data,
          error
        } = await supabase.from('menu_items').select('id, name, image_url').eq('restaurant_id', parseInt(selectedRestaurantId, 10)).eq('is_active', true).order('name');
        if (error) throw error;
        setMenuItems(data || []);
      } catch (err) {
        console.error('Error fetching menu items:', err);
      } finally {
        setIsLoadingMenu(false);
      }
    }
    fetchMenu();
  }, [selectedRestaurantId]);
  // Re-match images when menu items change
  useEffect(() => {
    if (images.length > 0 && menuItems.length > 0) {
      setImages(prev => prev.map(img => matchImage(img.file, img.preview, menuItems)));
    }
    if (docImages.length > 0 && menuItems.length > 0) {
      setDocImages(prev => prev.map(img => matchDocImage(img.file, img.preview, img.detectedTitle, menuItems)));
    }
  }, [menuItems]);
  // ─── Match Logic ───────────────────────────────────────────
  function matchImage(file: File, preview: string, items: MenuItemRow[]): ImageEntry {
    const originalName = removeExtension(file.name);
    const normalizedName = normalizeText(originalName);
    const {
      item: match,
      confidence
    } = findBestMatch(normalizedName, items);
    // Auto-match on exact or high confidence; no-match otherwise
    const isMatched = confidence === 'exact' || confidence === 'high';
    return {
      file,
      preview,
      originalName,
      normalizedName,
      matchedItem: isMatched ? match : null,
      status: isMatched ? 'matched' : 'no-match',
      uploading: false,
      uploaded: false,
      publicUrl: null,
      error: null
    };
  }
  function matchDocImage(file: File, preview: string, detectedTitle: string, items: MenuItemRow[]): DocImageEntry {
    const normalizedTitle = normalizeText(detectedTitle);
    const {
      item: match,
      confidence
    } = findBestMatch(normalizedTitle, items);
    let status: 'matched' | 'no-match' | 'manual-review' = 'manual-review';
    let matchedItem: MenuItemRow | null = null;
    if (confidence === 'exact' || confidence === 'high') {
      status = 'matched';
      matchedItem = match;
    } else if (confidence === 'medium') {
      // Close enough to suggest but needs review
      status = 'manual-review';
      matchedItem = match; // Pre-select as suggestion
    } else if (detectedTitle.trim()) {
      status = 'no-match';
    }
    return {
      file,
      preview,
      detectedTitle,
      normalizedTitle,
      matchedItem,
      status,
      uploading: false,
      uploaded: false,
      publicUrl: null,
      error: null
    };
  }
  // ─── Handle Files ──────────────────────────────────────────
  const processSingleFiles = useCallback((files: FileList | File[]) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const validFiles = Array.from(files).filter(f => validTypes.includes(f.type));
    const newEntries = validFiles.map(file => {
      const preview = URL.createObjectURL(file);
      return matchImage(file, preview, menuItems);
    });
    setImages(prev => [...prev, ...newEntries]);
    setApplyResult(null);
    setApplyError(null);
  }, [menuItems]);
  const processZipFile = async (file: File) => {
    setIsProcessingFile(true);
    setProcessingStatus('Extrayendo imágenes del ZIP...');
    try {
      const JSZip = await loadJSZip();
      const zip = new JSZip();
      const contents = await zip.loadAsync(file);
      const validExts = ['.jpg', '.jpeg', '.png', '.webp'];
      const extractedFiles: File[] = [];
      for (const [filename, zipEntry] of Object.entries(contents.files)) {
        if (zipEntry.dir) continue;
        const lowerName = filename.toLowerCase();
        if (validExts.some(ext => lowerName.endsWith(ext)) && !lowerName.includes('__macosx') && !lowerName.includes('.ds_store')) {
          const blob = await zipEntry.async('blob');
          // Extract just the filename without path
          const cleanName = filename.split('/').pop() || filename;
          const extractedFile = new File([blob], cleanName, {
            type: blob.type || 'image/jpeg'
          });
          extractedFiles.push(extractedFile);
        }
      }
      if (extractedFiles.length > 0) {
        processSingleFiles(extractedFiles);
      } else {
        setApplyError('No se encontraron imágenes válidas en el archivo ZIP.');
      }
    } catch (err) {
      console.error('Error processing ZIP:', err);
      setApplyError('Error al leer el archivo ZIP.');
    } finally {
      setIsProcessingFile(false);
      setProcessingStatus('');
    }
  };
  const processDocxFile = async (file: File) => {
    console.log('[DocImport] DOCX selected:', file.name, 'size:', file.size);
    setIsProcessingFile(true);
    setProcessingStatus('Cargando librería de documentos...');
    setApplyError(null);
    setApplyResult(null);
    try {
      // Step 1: Load mammoth
      console.log('[DocImport] Loading mammoth library...');
      let mammoth: any;
      try {
        mammoth = await loadMammoth();
        console.log('[DocImport] mammoth loaded:', typeof mammoth, Object.keys(mammoth || {}));
      } catch (libErr) {
        console.error('[DocImport] Failed to load mammoth:', libErr);
        setApplyError('Error al cargar la librería para documentos Word. Intenta recargar la página.');
        setIsProcessingFile(false);
        setProcessingStatus('');
        return;
      }
      if (!mammoth || typeof mammoth.convertToHtml !== 'function') {
        console.error('[DocImport] mammoth.convertToHtml not available. mammoth:', mammoth);
        setApplyError('La librería de documentos no se cargó correctamente. Intenta recargar la página.');
        setIsProcessingFile(false);
        setProcessingStatus('');
        return;
      }
      // Step 2: Read file
      setProcessingStatus('Leyendo documento...');
      console.log('[DocImport] Reading file as ArrayBuffer...');
      const arrayBuffer = await file.arrayBuffer();
      console.log('[DocImport] ArrayBuffer size:', arrayBuffer.byteLength);
      // Step 3: Convert to HTML
      setProcessingStatus('Convirtiendo documento a HTML...');
      console.log('[DocImport] Calling mammoth.convertToHtml...');
      const result = await mammoth.convertToHtml({
        arrayBuffer
      });
      const html = result.value || '';
      const warnings = result.messages || [];
      console.log('[DocImport] HTML length:', html.length);
      console.log('[DocImport] Warnings:', warnings.length, warnings);
      if (!html || html.trim().length === 0) {
        console.warn('[DocImport] Empty HTML output from mammoth');
        setApplyError('El documento no contiene contenido procesable.');
        setIsProcessingFile(false);
        setProcessingStatus('');
        return;
      }
      // Step 4: Parse HTML to find title + image pairs
      setProcessingStatus('Extrayendo imágenes del documento...');
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      // Collect ALL elements in document order (flatten the tree)
      const allElements = Array.from(doc.body.querySelectorAll('*'));
      const allImgs = Array.from(doc.body.querySelectorAll('img'));
      console.log('[DocImport] Total HTML elements:', allElements.length);
      console.log('[DocImport] Total <img> tags found:', allImgs.length);
      // Log first 500 chars of HTML for debugging
      console.log('[DocImport] HTML preview:', html.substring(0, 500));
      if (allImgs.length === 0) {
        console.warn('[DocImport] No <img> tags found in converted HTML');
        setApplyError('No se pudieron extraer imágenes del documento. El archivo puede no contener imágenes embebidas.');
        setIsProcessingFile(false);
        setProcessingStatus('');
        return;
      }
      const newEntries: DocImageEntry[] = [];
      let lastTitle = '';
      let titlesFound = 0;
      // Walk through ALL elements in document order to properly pair titles with images
      for (const el of allElements) {
        const tag = el.tagName;
        // If it's an image, extract it
        if (tag === 'IMG') {
          const src = el.getAttribute('src');
          if (src && (src.startsWith('data:image') || src.startsWith('blob:'))) {
            try {
              const imgFile = base64ToFile(src, `docx-${Date.now()}-${newEntries.length}.jpg`);
              const preview = URL.createObjectURL(imgFile);
              console.log('[DocImport] Extracted image #' + (newEntries.length + 1), 'title:', lastTitle || '(none)', 'size:', imgFile.size);
              newEntries.push(matchDocImage(imgFile, preview, lastTitle, menuItems));
              lastTitle = ''; // Reset after pairing
            } catch (imgErr) {
              console.warn('[DocImport] Failed to convert image:', imgErr);
            }
          }
          continue;
        }
        // If it's a text element (p, h1-h6, strong, span, td, li), capture text as potential title
        if (tag.match(/^(P|H[1-6]|STRONG|TD|LI)$/)) {
          // Only use direct text content (not from nested images)
          const directText = Array.from(el.childNodes).filter(n => n.nodeType === Node.TEXT_NODE || (n as Element).tagName !== 'IMG').map(n => n.textContent || '').join('').trim();
          if (directText && directText.length > 1 && directText.length < 200) {
            lastTitle = directText;
            titlesFound++;
          }
        }
      }
      console.log('[DocImport] Extraction complete. Images:', newEntries.length, 'Titles found:', titlesFound);
      console.log('[DocImport] Entries:', newEntries.map(e => ({
        title: e.detectedTitle,
        status: e.status,
        matched: e.matchedItem?.name
      })));
      if (newEntries.length > 0) {
        setDocImages(newEntries); // Replace, don't append
        setApplyResult(null);
        setApplyError(null);
        console.log('[DocImport] Preview ready with', newEntries.length, 'entries');
      } else {
        setApplyError('No se pudieron extraer imágenes del documento. Verifica que el archivo contenga imágenes embebidas.');
      }
    } catch (err: any) {
      console.error('[DocImport] DOCX processing error:', err);
      setApplyError(`Error al procesar el documento Word: ${err?.message || 'Error desconocido'}`);
    } finally {
      setIsProcessingFile(false);
      setProcessingStatus('');
    }
  };
  const processPdfFile = async (file: File) => {
    console.log('[DocImport] PDF selected:', file.name, 'size:', file.size);
    setIsProcessingFile(true);
    setProcessingStatus('Analizando documento PDF...');
    setApplyError(null);
    setApplyResult(null);
    try {
      // Step 1: Load pdfjs
      console.log('[DocImport] Loading pdfjs library...');
      let pdfjsLib: any;
      try {
        pdfjsLib = await loadPdfjs();
        console.log('[DocImport] pdfjs loaded:', typeof pdfjsLib);
      } catch (libErr) {
        console.error('[DocImport] Failed to load pdfjs:', libErr);
        setApplyError('La importación desde PDF aún no está disponible completamente. Por favor usa un archivo .docx en su lugar.');
        setIsProcessingFile(false);
        setProcessingStatus('');
        return;
      }
      if (!pdfjsLib || typeof pdfjsLib.getDocument !== 'function') {
        console.error('[DocImport] pdfjs.getDocument not available');
        setApplyError('La importación desde PDF aún no está disponible completamente. Por favor usa un archivo .docx en su lugar.');
        setIsProcessingFile(false);
        setProcessingStatus('');
        return;
      }
      // Step 2: Read PDF
      const arrayBuffer = await file.arrayBuffer();
      console.log('[DocImport] PDF ArrayBuffer size:', arrayBuffer.byteLength);
      const pdf = await pdfjsLib.getDocument({
        data: arrayBuffer
      }).promise;
      console.log('[DocImport] PDF pages:', pdf.numPages);
      const newEntries: DocImageEntry[] = [];
      let lastTitle = '';
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        console.log('[DocImport] Processing page', pageNum);
        const page = await pdf.getPage(pageNum);
        // Get text content
        const textContent = await page.getTextContent();
        const textItems = textContent.items.map((item: any) => item.str).filter((str: string) => str.trim().length > 0);
        console.log('[DocImport] Page', pageNum, 'text items:', textItems.length);
        if (textItems.length > 0) {
          lastTitle = textItems[textItems.length - 1];
        }
        // Get images
        const operatorList = await page.getOperatorList();
        const validObjIds: string[] = [];
        for (let i = 0; i < operatorList.fnArray.length; i++) {
          if (pdfjsLib.OPS && operatorList.fnArray[i] === pdfjsLib.OPS.paintImageXObject) {
            validObjIds.push(operatorList.argsArray[i][0]);
          }
        }
        console.log('[DocImport] Page', pageNum, 'image objects:', validObjIds.length);
        for (const objId of validObjIds) {
          try {
            const imgData = await page.objs.get(objId);
            if (imgData && imgData.data) {
              const canvas = document.createElement('canvas');
              canvas.width = imgData.width;
              canvas.height = imgData.height;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                const imgDataArray = new Uint8ClampedArray(imgData.data);
                let finalData = imgDataArray;
                if (imgDataArray.length === imgData.width * imgData.height * 3) {
                  finalData = new Uint8ClampedArray(imgData.width * imgData.height * 4);
                  for (let j = 0, k = 0; j < imgDataArray.length; j += 3, k += 4) {
                    finalData[k] = imgDataArray[j];
                    finalData[k + 1] = imgDataArray[j + 1];
                    finalData[k + 2] = imgDataArray[j + 2];
                    finalData[k + 3] = 255;
                  }
                }
                const imageData = new ImageData(finalData, imgData.width, imgData.height);
                ctx.putImageData(imageData, 0, 0);
                const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
                if (blob) {
                  const imgFile = new File([blob], `pdf-extract-${Date.now()}-${pageNum}-${objId}.jpg`, {
                    type: 'image/jpeg'
                  });
                  const preview = URL.createObjectURL(imgFile);
                  console.log('[DocImport] Extracted PDF image, page:', pageNum, 'title:', lastTitle || '(none)');
                  newEntries.push(matchDocImage(imgFile, preview, lastTitle, menuItems));
                  lastTitle = '';
                }
              }
            }
          } catch (e) {
            console.warn('[DocImport] Failed to extract image from PDF page', pageNum, e);
          }
        }
      }
      console.log('[DocImport] PDF extraction complete. Images:', newEntries.length);
      if (newEntries.length > 0) {
        setDocImages(newEntries);
        setApplyResult(null);
        setApplyError(null);
      } else {
        setApplyError('No se pudieron extraer imágenes del PDF. La importación desde PDF puede no funcionar con todos los documentos. Prueba con un archivo .docx.');
      }
    } catch (err: any) {
      console.error('[DocImport] PDF processing error:', err);
      setApplyError(`Error al procesar el PDF: ${err?.message || 'Error desconocido'}. Prueba con un archivo .docx.`);
    } finally {
      setIsProcessingFile(false);
      setProcessingStatus('');
    }
  };
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      handleIncomingFiles(e.dataTransfer.files);
    }
  }, [importMode, menuItems]);
  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleIncomingFiles(e.target.files);
      e.target.value = '';
    }
  }, [importMode, menuItems]);
  const handleIncomingFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    console.log('[MenuImages] handleIncomingFiles called. mode:', importMode, 'files:', fileArray.map(f => f.name));
    if (fileArray.length === 0) {
      console.warn('[MenuImages] No files received');
      return;
    }
    setApplyError(null);
    if (importMode === 'single') {
      processSingleFiles(fileArray);
    } else if (importMode === 'zip') {
      const zipFile = fileArray.find(f => f.name.toLowerCase().endsWith('.zip'));
      if (zipFile) {
        console.log('[MenuImages] ZIP file found:', zipFile.name);
        processZipFile(zipFile);
      } else {
        console.warn('[MenuImages] No .zip file in selection');
        setApplyError('Por favor selecciona un archivo .zip');
      }
    } else if (importMode === 'doc') {
      const docFile = fileArray.find(f => {
        const name = f.name.toLowerCase();
        return name.endsWith('.docx') || name.endsWith('.pdf');
      });
      if (docFile) {
        console.log('[MenuImages] Document file found:', docFile.name);
        if (docFile.name.toLowerCase().endsWith('.docx')) {
          processDocxFile(docFile);
        } else {
          processPdfFile(docFile);
        }
      } else {
        console.warn('[MenuImages] No .docx or .pdf file in selection');
        setApplyError('Por favor selecciona un archivo .docx o .pdf');
      }
    }
  };
  const removeImage = (index: number) => {
    if (importMode === 'doc') {
      setDocImages(prev => {
        const removed = prev[index];
        URL.revokeObjectURL(removed.preview);
        return prev.filter((_, i) => i !== index);
      });
    } else {
      setImages(prev => {
        const removed = prev[index];
        URL.revokeObjectURL(removed.preview);
        return prev.filter((_, i) => i !== index);
      });
    }
  };
  const manualMatch = (index: number, itemId: string) => {
    const item = menuItems.find(m => m.id === itemId) || null;
    if (importMode === 'doc') {
      setDocImages(prev => prev.map((entry, idx) => idx === index ? {
        ...entry,
        matchedItem: item,
        status: item ? 'matched' : 'no-match'
      } : entry));
    } else {
      setImages(prev => prev.map((entry, idx) => idx === index ? {
        ...entry,
        matchedItem: item,
        status: item ? 'matched' : 'no-match'
      } : entry));
    }
  };
  const clearAll = () => {
    if (importMode === 'doc') {
      docImages.forEach(img => URL.revokeObjectURL(img.preview));
      setDocImages([]);
    } else {
      images.forEach(img => URL.revokeObjectURL(img.preview));
      setImages([]);
    }
    setApplyResult(null);
    setApplyError(null);
  };
  // ─── Apply Images ──────────────────────────────────────────
  const handleApply = async () => {
    const currentList = importMode === 'doc' ? docImages : images;
    const matchedImages = currentList.filter(img => img.status === 'matched' && img.matchedItem);
    console.log('[MenuImages] Apply clicked. mode:', importMode, 'total:', currentList.length, 'matched:', matchedImages.length);
    if (matchedImages.length === 0) {
      console.warn('[MenuImages] No matched images to apply');
      setApplyError('No hay imágenes con coincidencia para aplicar. Asigna productos manualmente o sube imágenes con nombres que coincidan.');
      return;
    }
    setIsApplying(true);
    setApplyResult(null);
    setApplyError(null);
    const uploadedFiles: {
      file_name: string;
      public_url: string;
    }[] = [];
    const manualUpdates: {
      id: string;
      public_url: string;
    }[] = [];
    // Step 1: Upload all matched images to Supabase Storage
    for (let i = 0; i < currentList.length; i++) {
      const img = currentList[i];
      if (img.status !== 'matched' || !img.matchedItem) continue;
      const updateState = (updates: any) => {
        if (importMode === 'doc') {
          setDocImages(prev => prev.map((entry, idx) => idx === i ? {
            ...entry,
            ...updates
          } : entry));
        } else {
          setImages(prev => prev.map((entry, idx) => idx === i ? {
            ...entry,
            ...updates
          } : entry));
        }
      };
      updateState({
        uploading: true
      });
      try {
        const ext = img.file.name.split('.').pop() || 'jpg';
        const timestamp = Date.now();
        let safeName = '';
        if ('originalName' in img) {
          safeName = safeFileName((img as ImageEntry).originalName);
        } else {
          safeName = safeFileName((img as DocImageEntry).detectedTitle || `doc-img-${i}`);
        }
        if (!safeName) safeName = `img-${i}`;
        const path = `restaurant-${selectedRestaurantId}/${timestamp}-${safeName}.${ext}`;
        const {
          error: uploadError
        } = await supabase.storage.from('menu-images').upload(path, img.file, {
          upsert: true,
          contentType: img.file.type
        });
        if (uploadError) throw uploadError;
        const {
          data: urlData
        } = supabase.storage.from('menu-images').getPublicUrl(path);
        const publicUrl = urlData.publicUrl;
        // If it was manually matched (we can tell if it's a doc image or if the original name doesn't match),
        // we might want to track it for direct update, but the edge function handles it by filename.
        // Wait, the edge function matches by `file_name`.
        // For doc images or manual matches, the `file_name` we send might not match the menu item name automatically in the edge function.
        // Actually, if it's a manual match, the edge function won't know which item to update unless we pass the item ID.
        // Since the prompt says: "for manual matches: if easier, update public.menu_items.image_url directly from frontend"
        // Let's check if it's an automatic match or manual.
        let isAutoMatch = false;
        if ('originalName' in img) {
          isAutoMatch = normalizeText((img as ImageEntry).originalName) === normalizeText(img.matchedItem.name);
        } else {
          isAutoMatch = normalizeText((img as DocImageEntry).detectedTitle) === normalizeText(img.matchedItem.name);
        }
        if (isAutoMatch) {
          uploadedFiles.push({
            file_name: 'originalName' in img ? (img as ImageEntry).originalName : (img as DocImageEntry).detectedTitle,
            public_url: publicUrl
          });
        } else {
          // Manual match
          manualUpdates.push({
            id: img.matchedItem.id,
            public_url: publicUrl
          });
        }
        updateState({
          uploading: false,
          uploaded: true,
          publicUrl,
          error: null
        });
      } catch (err: any) {
        console.error(`Error uploading:`, err);
        updateState({
          uploading: false,
          error: err.message || 'Error al subir'
        });
      }
    }
    let totalUpdated = 0;
    let totalMatched = 0;
    let totalUnmatched = 0;
    // Step 2: Call edge function for auto matches
    if (uploadedFiles.length > 0) {
      try {
        const payload = {
          restaurant_id: parseInt(selectedRestaurantId, 10),
          files: uploadedFiles
        };
        console.log('[MenuImages] Sending to apply-menu-images:', payload);
        const response = await fetch(`${SUPABASE_URL}/functions/v1/apply-menu-images`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        console.log('[MenuImages] Response:', result);
        if (!response.ok) {
          setApplyError(result?.error || result?.message || `Error del servidor (${response.status})`);
        } else if (result?.success) {
          totalMatched += result.matched ?? uploadedFiles.length;
          totalUpdated += result.updated ?? uploadedFiles.length;
          totalUnmatched += result.unmatched ?? 0;
        } else {
          setApplyError(result?.error || 'Error al aplicar imágenes.');
        }
      } catch (err: any) {
        console.error('[MenuImages] Edge function error:', err);
        setApplyError(err.message || 'Error de conexión con el servidor.');
      }
    }
    // Step 3: Direct DB updates for manual matches
    if (manualUpdates.length > 0) {
      try {
        for (const update of manualUpdates) {
          const {
            error
          } = await supabase.from('menu_items').update({
            image_url: update.public_url
          }).eq('id', update.id);
          if (error) throw error;
          totalMatched++;
          totalUpdated++;
        }
      } catch (err: any) {
        console.error('[MenuImages] Manual update error:', err);
        setApplyError(prev => prev ? `${prev} | Error en actualización manual` : 'Error en actualización manual');
      }
    }
    if (!applyError && (uploadedFiles.length > 0 || manualUpdates.length > 0)) {
      setApplyResult({
        total: uploadedFiles.length + manualUpdates.length,
        matched: totalMatched,
        updated: totalUpdated,
        unmatched: totalUnmatched
      });
    }
    setIsApplying(false);
  };
  // ─── Stats ─────────────────────────────────────────────────
  const currentList = importMode === 'doc' ? docImages : images;
  const matchedCount = currentList.filter(i => i.status === 'matched').length;
  const unmatchedCount = currentList.filter(i => i.status === 'no-match').length;
  const manualReviewCount = currentList.filter(i => i.status === 'manual-review').length;
  const uploadedCount = currentList.filter(i => i.uploaded).length;
  // ─── Render ────────────────────────────────────────────────
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans selection:bg-emerald-500/30" data-id="element-2882">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12 space-y-8" data-id="element-2883">
        {/* Header */}
        <header className="flex items-start gap-4" data-id="element-2884">
          <button onClick={() => navigate('/admin/enrollment')} className="w-10 h-10 bg-[#141414] border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors shrink-0 mt-2" data-id="element-2885">
            <ArrowLeft className="w-5 h-5" data-id="element-2886" />
          </button>
          <div className="w-14 h-14 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center shrink-0" data-id="element-2887">
            <ImageIcon className="w-7 h-7 text-emerald-500" data-id="element-2888" />
          </div>
          <div data-id="element-2889">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-id="element-2890">
              Importar imágenes de menú
            </h1>
            <p className="text-white/40 text-sm mt-1 max-w-xl" data-id="element-2891">
              Sube imágenes nombradas como los productos y se asignarán
              automáticamente.
            </p>
          </div>
        </header>

        {/* Restaurant Selector */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-5" data-id="element-2892">
          <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-2893">
            Restaurante
          </label>
          <select value={selectedRestaurantId} onChange={e => {
          setSelectedRestaurantId(e.target.value);
          setImages([]);
          setDocImages([]);
          setApplyResult(null);
          setApplyError(null);
        }} disabled={isLoadingRestaurants} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-emerald-500 disabled:opacity-50 appearance-none" data-id="element-2894">
            <option value="" disabled data-id="element-2895">
              Selecciona un restaurante...
            </option>
            {restaurants.map(r => <option key={r.id} value={r.id} data-id="element-2896">
                {r.name} — /{r.slug}
              </option>)}
          </select>

          {isLoadingMenu && <div className="flex items-center gap-2 mt-3 text-white/30 text-sm" data-id="element-2897">
              <Loader2 className="w-4 h-4 animate-spin" data-id="element-2898" />
              Cargando productos...
            </div>}
          {!isLoadingMenu && selectedRestaurantId && menuItems.length > 0 && <p className="text-white/30 text-xs mt-3" data-id="element-2899">
              {menuItems.length} productos encontrados
            </p>}
        </div>

        {/* Mode Selector */}
        {selectedRestaurantId && !isLoadingMenu && menuItems.length > 0 && <div className="space-y-4" data-id="element-2900">
            <label className="block text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-2901">
              Modo de importación
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-2902">
              <button onClick={() => setImportMode('single')} className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition-all ${importMode === 'single' ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-400' : 'bg-[#141414] border-white/10 text-white/60 hover:border-white/20 hover:bg-white/5'}`} data-id="element-2903">
                <ImageIcon className="w-6 h-6" data-id="element-2904" />
                <span className="text-sm font-bold" data-id="element-2905">Imágenes sueltas</span>
              </button>
              <button onClick={() => setImportMode('zip')} className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition-all ${importMode === 'zip' ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-400' : 'bg-[#141414] border-white/10 text-white/60 hover:border-white/20 hover:bg-white/5'}`} data-id="element-2906">
                <Archive className="w-6 h-6" data-id="element-2907" />
                <span className="text-sm font-bold" data-id="element-2908">ZIP de imágenes</span>
              </button>
              <button onClick={() => setImportMode('doc')} className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition-all ${importMode === 'doc' ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-400' : 'bg-[#141414] border-white/10 text-white/60 hover:border-white/20 hover:bg-white/5'}`} data-id="element-2909">
                <FileText className="w-6 h-6" data-id="element-2910" />
                <span className="text-sm font-bold" data-id="element-2911">Documento Word / PDF</span>
              </button>
            </div>

            {/* Helper Text */}
            <div className="flex items-start gap-3 p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl" data-id="element-2912">
              <InfoIcon className="w-4 h-4 text-emerald-500/60 shrink-0 mt-0.5" data-id="element-2913" />
              <p className="text-xs text-white/50 leading-relaxed" data-id="element-2914">
                {importMode === 'single' && 'Sube imágenes individualmente. El nombre del archivo debe coincidir con el nombre del producto.'}
                {importMode === 'zip' && 'Sube un archivo .zip con múltiples imágenes. Se extraerán y asociarán por nombre de archivo.'}
                {importMode === 'doc' && 'Sube un documento con nombres e imágenes del menú. Intentaremos extraer las imágenes y asociarlas al título más cercano.'}
              </p>
            </div>
          </div>}

        {/* Drop Zone */}
        {selectedRestaurantId && !isLoadingMenu && menuItems.length > 0 && <div onDragOver={e => {
        e.preventDefault();
        setIsDragging(true);
      }} onDragLeave={() => setIsDragging(false)} onDrop={handleDrop} onClick={() => !isProcessingFile && fileInputRef.current?.click()} className={`border-2 border-dashed rounded-2xl p-8 sm:p-12 text-center transition-all ${isProcessingFile ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${isDragging ? 'border-emerald-500/60 bg-emerald-500/5' : 'border-white/10 hover:border-white/20 bg-[#141414]'}`} data-id="element-2915">
            {isProcessingFile ? <div className="flex flex-col items-center justify-center" data-id="element-2916">
                <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-3" data-id="element-2917" />
                <p className="text-sm text-white/70 font-medium" data-id="element-2918">
                  {processingStatus}
                </p>
              </div> : <>
                <Upload className={`w-8 h-8 mx-auto mb-3 ${isDragging ? 'text-emerald-500' : 'text-white/20'}`} data-id="element-2919" />
                <p className="text-sm text-white/50 font-medium" data-id="element-2920">
                  Arrastra{' '}
                  {importMode === 'single' ? 'imágenes' : importMode === 'zip' ? 'tu archivo ZIP' : 'tu documento'}{' '}
                  aquí o haz click para seleccionar
                </p>
                <p className="text-xs text-white/25 mt-1" data-id="element-2921">
                  {importMode === 'single' && 'JPG, PNG o WebP'}
                  {importMode === 'zip' && 'Archivo .zip'}
                  {importMode === 'doc' && 'Archivo .docx o .pdf'}
                </p>
              </>}
            <input ref={fileInputRef} type="file" accept={importMode === 'single' ? 'image/jpeg,image/png,image/webp' : importMode === 'zip' ? '.zip,application/zip' : '.docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'} multiple={importMode === 'single'} onChange={handleFileInput} className="hidden" data-id="element-2922" />
          </div>}

        {/* Preview Table */}
        {currentList.length > 0 && <div className="space-y-4" data-id="element-2923">
            {/* Stats bar */}
            <div className="flex items-center justify-between" data-id="element-2924">
              <div className="flex flex-wrap gap-4 text-xs" data-id="element-2925">
                <span className="text-white/40" data-id="element-2926">
                  {currentList.length} imagen
                  {currentList.length !== 1 ? 'es' : ''}
                </span>
                <span className="text-emerald-400" data-id="element-2927">
                  {matchedCount} coincidencia{matchedCount !== 1 ? 's' : ''}
                </span>
                {unmatchedCount > 0 && <span className="text-amber-400" data-id="element-2928">
                    {unmatchedCount} sin match
                  </span>}
                {manualReviewCount > 0 && <span className="text-blue-400" data-id="element-2929">
                    {manualReviewCount} revisión manual
                  </span>}
                {uploadedCount > 0 && <span className="text-emerald-300" data-id="element-2930">
                    {uploadedCount} subida{uploadedCount !== 1 ? 's' : ''}
                  </span>}
              </div>
              <button onClick={clearAll} className="text-xs text-white/30 hover:text-white/50 transition-colors shrink-0" data-id="element-2931">
                Limpiar todo
              </button>
            </div>

            {/* Image rows */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden divide-y divide-white/5" data-id="element-2932">
              {currentList.map((img, idx) => {
            const isDoc = importMode === 'doc';
            const title = isDoc ? (img as DocImageEntry).detectedTitle : (img as ImageEntry).originalName;
            const normalized = isDoc ? (img as DocImageEntry).normalizedTitle : (img as ImageEntry).normalizedName;
            return <div key={idx} className="flex items-center gap-3 px-4 py-3" data-id="element-2933">
                    {/* Thumbnail */}
                    <img src={img.preview} alt={title || 'Preview'} className="w-12 h-12 rounded-lg object-cover shrink-0 bg-[#0B0B0B]" data-id="element-2934" />

                    {/* Info */}
                    <div className="flex-1 min-w-0" data-id="element-2935">
                      <p className="text-sm font-medium text-white/80 truncate" data-id="element-2936">
                        {title || <span className="italic text-white/30" data-id="element-2937">
                            Sin título detectado
                          </span>}
                      </p>
                      {normalized && <p className="text-[11px] text-white/25 truncate" data-id="element-2938">
                          {normalized}
                        </p>}
                      {img.status === 'matched' && img.matchedItem ? <p className="text-xs text-emerald-400/70 truncate mt-0.5" data-id="element-2939">
                          → {img.matchedItem.name}
                        </p> : img.status === 'manual-review' && img.matchedItem && !img.uploaded ? <div className="mt-1" data-id="element-2940">
                          <p className="text-[11px] text-blue-400/70 mb-1" data-id="element-2941">
                            Sugerencia: {img.matchedItem.name}
                          </p>
                          <select value={img.matchedItem.id} onChange={e => manualMatch(idx, e.target.value)} className="w-full max-w-[220px] bg-[#0B0B0B] border border-blue-500/20 rounded-lg px-2 py-1 text-xs text-white/60 focus:outline-none focus:border-blue-500 appearance-none" data-id="element-2942">
                            <option value="" disabled data-id="element-2943">
                              Cambiar producto...
                            </option>
                            {menuItems.map(mi => <option key={mi.id} value={mi.id} data-id="element-2944">
                                {mi.name}
                              </option>)}
                          </select>
                        </div> : !img.uploaded ? <select value="" onChange={e => manualMatch(idx, e.target.value)} className="mt-1 w-full max-w-[220px] bg-[#0B0B0B] border border-white/10 rounded-lg px-2 py-1 text-xs text-white/60 focus:outline-none focus:border-amber-500 appearance-none" data-id="element-2945">
                          <option value="" disabled data-id="element-2946">
                            Asignar producto...
                          </option>
                          {menuItems.map(mi => <option key={mi.id} value={mi.id} data-id="element-2947">
                              {mi.name}
                            </option>)}
                        </select> : <p className="text-xs text-amber-400/70 mt-0.5" data-id="element-2948">
                          Sin coincidencia
                        </p>}
                    </div>

                    {/* Status */}
                    <div className="shrink-0 flex items-center gap-2" data-id="element-2949">
                      {img.uploading && <Loader2 className="w-4 h-4 text-white/30 animate-spin" data-id="element-2950" />}
                      {img.uploaded && !img.error && <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-2951" />}
                      {img.error && <span className="text-[10px] text-red-400 max-w-[100px] truncate" data-id="element-2952">
                          {img.error}
                        </span>}
                      {!img.uploading && !img.uploaded && !img.error && <>
                          {img.status === 'matched' && <CheckCircle2 className="w-4 h-4 text-emerald-500/50" data-id="element-2953" />}
                          {img.status === 'no-match' && <AlertTriangle className="w-4 h-4 text-amber-500/50" data-id="element-2954" />}
                          {img.status === 'manual-review' && <Search className="w-4 h-4 text-blue-500/50" data-id="element-2955" />}
                        </>}
                      <button onClick={() => removeImage(idx)} className="w-7 h-7 rounded-lg flex items-center justify-center text-white/20 hover:text-white/50 hover:bg-white/5 transition-colors" data-id="element-2956">
                        <Trash2 className="w-3.5 h-3.5" data-id="element-2957" />
                      </button>
                    </div>
                  </div>;
          })}
            </div>

            {/* Apply Button */}
            <button onClick={handleApply} disabled={matchedCount === 0 || isApplying} className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 disabled:bg-white/5 disabled:text-white/30 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95" data-id="element-2958">
              {isApplying ? <>
                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-2959" />
                  Aplicando imágenes...
                </> : <>
                  <ImageIcon className="w-5 h-5" data-id="element-2960" />
                  Aplicar {matchedCount} imagen{matchedCount !== 1 ? 'es' : ''}
                </>}
            </button>

            {/* Error */}
            {applyError && <div className="p-4 rounded-xl border bg-red-500/10 border-red-500/20" data-id="element-2961">
                <p className="text-sm font-medium text-red-400" data-id="element-2962">{applyError}</p>
              </div>}

            {/* Success Result */}
            {applyResult && <div className="p-5 rounded-xl border bg-emerald-500/10 border-emerald-500/20 space-y-3" data-id="element-2963">
                <div className="flex items-center gap-2" data-id="element-2964">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-2965" />
                  <p className="text-sm font-bold text-emerald-400" data-id="element-2966">
                    Imágenes aplicadas exitosamente
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-2967">
                  <div className="text-center" data-id="element-2968">
                    <p className="text-lg font-bold text-white" data-id="element-2969">
                      {applyResult.total}
                    </p>
                    <p className="text-[10px] text-white/30 uppercase tracking-widest" data-id="element-2970">
                      Total
                    </p>
                  </div>
                  <div className="text-center" data-id="element-2971">
                    <p className="text-lg font-bold text-emerald-400" data-id="element-2972">
                      {applyResult.matched}
                    </p>
                    <p className="text-[10px] text-white/30 uppercase tracking-widest" data-id="element-2973">
                      Matched
                    </p>
                  </div>
                  <div className="text-center" data-id="element-2974">
                    <p className="text-lg font-bold text-emerald-300" data-id="element-2975">
                      {applyResult.updated}
                    </p>
                    <p className="text-[10px] text-white/30 uppercase tracking-widest" data-id="element-2976">
                      Updated
                    </p>
                  </div>
                  <div className="text-center" data-id="element-2977">
                    <p className="text-lg font-bold text-amber-400" data-id="element-2978">
                      {applyResult.unmatched}
                    </p>
                    <p className="text-[10px] text-white/30 uppercase tracking-widest" data-id="element-2979">
                      Unmatched
                    </p>
                  </div>
                </div>
              </div>}
          </div>}
      </div>
    </div>;
}